import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Sidebar, useSidebar } from '@/components/ui/sidebar';
import { SidebarCollapseButton } from '@/components/sidebar-collapse-button';
import { cn } from '@/lib/utils';

/**
 * Shared sidebar shell used by every surface's sidebar (workspace, org,
 * super-admin).
 *
 * Desktop (≥768px): the manual `collapsible="none"` icon-rail. `none` (NOT
 * base-ui `icon`/`offcanvas`) is deliberate: those modes wrap the panel in
 * `position: fixed`, which breaks the base-ui Combobox switchers' real-pointer
 * toggle. We reproduce the icon rail via `data-collapsible="icon"` + `group`.
 *
 * Mobile (<768px): pass a non-`none` collapsible so ui/sidebar.tsx takes its
 * built-in Sheet-drawer branch. Drawer width comes from SIDEBAR_WIDTH_MOBILE
 * inside ui/sidebar.tsx — this frame's width/classes never reach the Sheet.
 * The desktop rail is ALSO CSS-hidden below md: useIsMobile() is false on
 * first paint, so without `hidden md:flex` a phone would flash the rail.
 */
export function SidebarFrame({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const { state, isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const collapsed = state === 'collapsed';

  // Close the mobile drawer on ANY route change — one shared effect instead
  // of per-link handlers, so programmatic navigations (workspace switch,
  // org go-back) are covered too.
  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  if (isMobile) {
    return <Sidebar collapsible="offcanvas">{children}</Sidebar>;
  }

  return (
    <Sidebar
      collapsible="none"
      data-collapsible={collapsed ? 'icon' : undefined}
      style={{ width: collapsed ? 'var(--sidebar-width-icon)' : 'var(--sidebar-width)' }}
      className={cn(
        'group relative hidden shrink-0 transition-[width] duration-200 ease-linear md:flex',
        className,
      )}
    >
      {children}
      {/* Circular toggle on the right edge — collapse to the icon rail and back. */}
      <SidebarCollapseButton />
    </Sidebar>
  );
}
